import { useState } from "react";
import { ShieldAlert, Download, Trash2, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, openAuthedFile } from "@/api/client";
import { formatDate } from "@/lib/format";
import { useAuthStore } from "@/stores/authStore";

/**
 * GDPR panel (Block 3 B3.7) on the customer detail — data export (portability) + right to be
 * forgotten (anonymize in place). Erasure is admin-only and double-confirmed; once anonymized the
 * panel shows the tombstone state. Exports stream via the authed-file helper.
 */
export default function GdprPanel({
  customerId,
  anonymizedAt,
  onAnonymized,
}: {
  customerId: string;
  anonymizedAt?: string | null;
  onAnonymized?: () => void;
}) {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === "admin";
  // Mirror the server's canViewPassport gate (admin/editor/sales) — don't show Export buttons the
  // API will 403 for marketing/readonly users.
  const canExport = role === "admin" || role === "editor" || role === "sales";
  const [busy, setBusy] = useState<"json" | "csv" | "forget" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function exportData(format: "json" | "csv") {
    setBusy(format);
    setMsg(null);
    try {
      await openAuthedFile(
        `/api/admin/gdpr/export?customerId=${customerId}&format=${format}`,
        `gdpr-${customerId}.${format}`,
      );
    } catch (e) {
      setMsg(e instanceof Error ? e.message : t("crm.gdpr.exportFailed"));
    } finally {
      setBusy(null);
    }
  }

  async function anonymize() {
    if (!window.confirm(t("crm.gdpr.anonymizeConfirm"))) return;
    setBusy("forget");
    setMsg(null);
    try {
      await api.post(`/api/admin/gdpr/forget?customerId=${customerId}&confirm=1`);
      onAnonymized?.();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : t("crm.gdpr.anonymizeFailed"));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card">
      <div className="mb-4 flex items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-gray-500" />
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          {t("crm.gdpr.title")}
        </h3>
        {anonymizedAt && (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            <CheckCircle2 className="h-3 w-3" /> {t("crm.gdpr.anonymized")}{" "}
            {formatDate(anonymizedAt)}
          </span>
        )}
      </div>

      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">{t("crm.gdpr.description")}</p>

      <div className="flex flex-wrap items-center gap-3">
        {canExport && (
          <>
            <button
              type="button"
              onClick={() => exportData("json")}
              disabled={busy !== null}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Download className="h-4 w-4" /> {t("crm.gdpr.exportJson")}
            </button>
            <button
              type="button"
              onClick={() => exportData("csv")}
              disabled={busy !== null}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Download className="h-4 w-4" /> {t("crm.gdpr.exportCsv")}
            </button>
          </>
        )}
        {isAdmin && !anonymizedAt && (
          <button
            type="button"
            onClick={anonymize}
            disabled={busy !== null}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />{" "}
            {busy === "forget" ? t("crm.gdpr.anonymizing") : t("crm.gdpr.anonymize")}
          </button>
        )}
      </div>
      {!isAdmin && !anonymizedAt && (
        <p className="mt-2 text-xs text-gray-400">{t("crm.gdpr.adminOnly")}</p>
      )}
      {msg && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{msg}</p>}
    </div>
  );
}
